/**
 * https://docs.dndkit.com/presets/sortable#drag-overlay
 * */
import React, { useState } from "react";
import { useEffect } from "react";
import {
    rectIntersection,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import toast, { Toaster } from "react-hot-toast";
import Button from "react-bootstrap/Button";
import Chance from "chance";
import axios from "axios";
import Modal from "react-bootstrap/Modal";

const endpoint = "/api/1";

const candidate_img_style = {
    width: "20em",
    margin: "auto",
    padding: "1em",
};

const default_candidate_img_url = "/static/images/mit_beaver.png";

function Item({ name, img_url, open_application_callback }) {
    const div_style = {
        display: "flex",
        background: "whitesmoke",
        outline: "gray solid",
        "flex-direction": "column",
        "text-align": "center",
        "margin-left": "auto",
        "margin-right": "auto",
        "margin-top": "1em",
        "margin-bottom": "1em",
    };
    return (
        <div style={div_style}>
            <img src={img_url} style={candidate_img_style} />
            <p>
                <b>{name}</b>
            </p>
            <Button onClick={() => open_application_callback()}>
                Open application
            </Button>
        </div>
    );
}

function SortableItem(props) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: props.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const div_style = {
        width: "20em",
        display: "flex",
        "flex-direction": "column",
        "text-align": "center",
        "margin-left": "auto",
        "margin-right": "auto",
        "margin-top": "1em",
        "margin-bottom": "1em",
        opacity: isDragging ? 0.2 : 1.0,
    };

    return (
        <div style={div_style}>
            <div ref={setNodeRef} {...attributes} {...listeners} style={style}>
                <Item
                    name={props.name}
                    img_url={props.img_url}
                    open_application_callback={props.open_application_callback}
                />
            </div>
        </div>
    );
}

const ApplicationModal = ({ show, item, handleClose }) => {
    const item_name = item === null ? "" : item.name;
    const candidate = item === null ? {} : item.raw_candidate;
    return (
        <Modal className="modal-lg" show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>{item_name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {item !== null && (
                    <>
                        <div className="d-flex">
                            <img
                                src={item.img_url}
                                style={candidate_img_style}
                            />
                        </div>

                        {candidate.hall_specific_message !== null && (
                            <>
                                <h3>Message specific to your hall</h3>
                                <p>{candidate.hall_specific_message}</p>
                            </>
                        )}

                        <h3>Reasons for applying</h3>
                        <p>{candidate.reasons}</p>

                        <h3>Plans as a Hall Councilor</h3>
                        <p>{candidate.plans}</p>

                        <h3>Experience</h3>
                        <p>{candidate.experience}</p>

                        <h3>Involvement</h3>
                        <p>{candidate.involvement}</p>

                        <h3>Summer plans</h3>
                        <p>{candidate.summer_plans}</p>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * Component that holds two ordered lists. The first list contains a list of
 * options. The second list is initially empty. Users are meant to
 * drag elements from the first list to the second to convey their preferred
 * ordering of the available options.
 */
function DragAndDropForm({
    candidates,
    min_candidates,
    rng_seed,
    election_id,
    pass_code,
    initial_ranked_kerberoses, // null or string[]
    on_vote_submit,
}) {
    // List of all candidates. Filled when instantiated with stable shuffle
    // of candidates
    const [all_items, setAllItems] = useState([]);
    // Candidates in left column
    const [itemsAIds, setItemsAIds] = useState([]);
    // Candidates in right column (ranked vote)
    const [itemsBIds, setItemsBIds] = useState([]);

    const [showApplicationModal, setShowApplicationModal] = useState(false);
    const [applicationModalItem, setApplicationModalItem] = useState(null);
    const [isVoteBeingSubmitted, setIsVoteBeingSubmitted] = useState(false);

    // get inventory
    useEffect(() => {
        const setData = async () => {
            const processed_data = await Promise.all(
                candidates.map(async (candidate) => {
                    let img_url = default_candidate_img_url;
                    const kerberos = candidate.kerberos;
                    try {
                        const candidate_image_url = `${endpoint}/hall_councilor/candidate_image?election_id=${election_id}&kerberos=${kerberos}`;
                        // Try to fetch the image. If we get an error (e.g.,
                        // 404), we fall back to the default candidate image.
                        // Otherwise we use the candidate's image.
                        await axios.get(candidate_image_url);
                        img_url = candidate_image_url;
                    } catch (e) {
                        console.error(e);
                    }
                    return {
                        id: candidate.kerberos,
                        name: `${candidate.first_name} ${candidate.last_name}`,
                        img_url: img_url,
                        raw_candidate: candidate,
                        kerberos: candidate.kerberos,
                    };
                }),
            );

            // Stable-shuffle the data
            const chance = new Chance(rng_seed);
            const all_items = chance.shuffle(processed_data);

            setAllItems(all_items);

            if (initial_ranked_kerberoses === null) {
                // Left column contains every candidate
                setItemsAIds(all_items.map(({ id }) => id));
                setItemsBIds([]);
            } else {
                // Left column contains all non-ranked items
                const futureA = all_items.filter(
                    (item) =>
                        !initial_ranked_kerberoses.includes(item.kerberos),
                );

                // Right column has ranked candidates
                const futureB = [];
                for (const kerberos of initial_ranked_kerberoses) {
                    const matches = all_items.filter(
                        (item) => item.kerberos === kerberos,
                    );
                    if (matches.length > 0) {
                        const match = matches[0];
                        futureB.push(match);
                    } else {
                        console.error(
                            `Could not find applicant with kerberos ${kerberos} in applicant list, but was in saved vote!`,
                        );
                    }
                }
                console.log(futureB);

                setItemsAIds(futureA.map((item) => item.id));
                setItemsBIds(futureB.map((item) => item.id));
            }
        };

        setData();
    }, []);

    const [activeId, setActiveId] = useState(null);
    const containerA_id = "CONTA";
    const containerB_id = "CONTB";
    const { setNodeRefA } = useDroppable({
        id: containerA_id,
    });
    const { setNodeRefB } = useDroppable({
        id: containerB_id,
    });
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 10, // distance in pixels to emit a drag event
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    function handleDragStart(event) {
        setActiveId(event.active.id);
    }

    function handleDragEnd(event) {
        const { active, over } = event;
        const container_id = over.data.current.sortable.containerId;
        if (active.id !== over.id) {
            // Item moved within a list. Update the corresponding list order.
            const setItemsIds =
                container_id === containerA_id ? setItemsAIds : setItemsBIds;
            setItemsIds((item_ids) => {
                const oldIndex = item_ids.indexOf(active.id);
                const newIndex = item_ids.indexOf(over.id);

                return arrayMove(item_ids, oldIndex, newIndex);
            });
        }

        setActiveId(null);
    }

    function handleDragOver(event) {
        const { active, over } = event;
        const old_container_id = active.data.current.sortable.containerId;
        const new_container_id =
            over === null ? null : over.data.current.sortable.containerId;
        const changed_list =
            over === null || old_container_id !== new_container_id;

        if (changed_list) {
            // Item was moved from one list to the other. Update lists.
            const old_list =
                old_container_id === containerA_id ? itemsAIds : itemsBIds;
            const new_list =
                old_container_id === containerA_id ? itemsBIds : itemsAIds;
            const setOldList =
                old_container_id === containerA_id
                    ? setItemsAIds
                    : setItemsBIds;
            const setNewList =
                old_container_id === containerA_id
                    ? setItemsBIds
                    : setItemsAIds;
            const oldIndex = old_list.indexOf(active.id);
            const item = old_list[oldIndex];
            setOldList(old_list.filter((item_id) => item_id !== activeId));
            setNewList([...new_list, item]);
        }
    }

    // Find active item
    const items = all_items.map((item) => (
        <Item
            name={item.name}
            img_url={item.img_url}
            open_application_callback={() => openApplicationModal(item)}
        />
    ));
    const all_ids = activeId ? all_items.map((item) => item.id) : null;
    const active_item_index = all_ids ? all_ids.indexOf(activeId) : null;
    // But for some reason the correct line
    // (immediately below this comment) produces incorrect behavior
    // exactly when active_item_index is zero, for reasons I cannot
    // take the time to understand right now, so I'll do a hack instead,
    // which is semantically correct.
    //const active_item = active_item_index ? items[active_item_index] : null;
    const active_item = active_item_index
        ? items[active_item_index]
        : items.length > 0
        ? items[0]
        : null;

    const column_style = {
        background: "white",
        "margin-left": "auto",
        "margin-right": "auto",
        overflow: "scroll",
        "min-height": "10vh",
        outline: "dashed grey",
        "margin-top": "1em",
    };

    const item_map = new Map();
    for (const item of all_items) {
        item_map.set(item.id, item);
    }

    async function handleVote() {
        // Check the minimum number of candidates have been selected
        const actual_min_candidates = Math.min(
            min_candidates,
            all_items.length,
        );
        if (itemsBIds.length < actual_min_candidates) {
            toast.error(
                `You must choose at least ${actual_min_candidates} candidates!`,
            );
            return;
        }

        // Simple way to turn athena email (xyz@mit.edu) into kerberos (xyz)
        const ranked_candidate_kerberoses = itemsBIds.map(
            (item_id) => item_map.get(item_id).kerberos,
        );
        const vote = {
            election_id: election_id,
            pass_code: pass_code,
            ranked_candidate_kerberoses: ranked_candidate_kerberoses,
        };

        // Start request
        setIsVoteBeingSubmitted(true);
        const response = axios.post(`${endpoint}/hall_councilor/vote`, {
            ...vote,
        });

        toast.promise(response, {
            loading: "Submitting vote...",
            success: "Successfully submitted vote!",
            error: "Failed to submit vote!",
        });

        try {
            const { data } = await response;
            const pass_code = data.pass_code;
            on_vote_submit(pass_code);
        } catch (e) {
            console.error(e);
        } finally {
            setIsVoteBeingSubmitted(false);
        }
    }

    function openApplicationModal(item) {
        setApplicationModalItem(item);
        setShowApplicationModal(true);
    }

    function format_ordinal(i) {
        const last_digit = i % 10;
        const suffix =
            last_digit === 0
                ? "th"
                : last_digit === 1
                ? "st"
                : last_digit === 2
                ? "nd"
                : last_digit === 3
                ? "rd"
                : "th";
        return `${i}${suffix}`;
    }

    return (
        <div>
            <div className="row">
                <div
                    className="col-md-3"
                    style={{ margin: "auto", width: "6em" }}
                >
                    <Button
                        onClick={() => handleVote()}
                        disabled={isVoteBeingSubmitted}
                    >
                        {!isVoteBeingSubmitted && "Submit"}
                        {isVoteBeingSubmitted && (
                            <div className="spinner-border" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                        )}
                    </Button>
                </div>
            </div>
            <p>
                Drag and drop candidates from the left list to the right list to
                rank them. Your first choice should be at the top of the list.
            </p>
            <div className="row" style={{ "padding-top": "1em" }}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={rectIntersection}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                >
                    <div className="col-md-3" style={column_style}>
                        <h3>
                            Candidate List ({itemsAIds.length}/
                            {all_items.length} not ranked)
                        </h3>
                        <SortableContext
                            items={itemsAIds}
                            strategy={verticalListSortingStrategy}
                            id={containerA_id}
                        >
                            <div ref={setNodeRefA}>
                                {itemsAIds.map((item_id) => {
                                    const item = item_map.get(item_id);
                                    return (
                                        <SortableItem
                                            id={item.id}
                                            name={item.name}
                                            img_url={item.img_url}
                                            application_url={
                                                item.application_url
                                            }
                                            open_application_callback={() =>
                                                openApplicationModal(item)
                                            }
                                        />
                                    );
                                })}
                            </div>
                        </SortableContext>
                    </div>
                    <div className="col-md-3" style={column_style}>
                        <h3>
                            My ranking ({itemsBIds.length}/{all_items.length}{" "}
                            ranked)
                        </h3>
                        <SortableContext
                            items={itemsBIds}
                            strategy={verticalListSortingStrategy}
                            id={containerB_id}
                        >
                            <div ref={setNodeRefB}>
                                {itemsBIds.map((item_id, i) => {
                                    const item = item_map.get(item_id);
                                    return (
                                        <SortableItem
                                            id={item.id}
                                            name={`${
                                                item.name
                                            } (${format_ordinal(
                                                i + 1,
                                            )} choice)`}
                                            img_url={item.img_url}
                                            application_url={
                                                item.application_url
                                            }
                                            open_application_callback={() =>
                                                openApplicationModal(item)
                                            }
                                        />
                                    );
                                })}
                            </div>
                        </SortableContext>
                    </div>
                    <DragOverlay>{active_item}</DragOverlay>
                </DndContext>
            </div>
            <ApplicationModal
                show={showApplicationModal}
                item={applicationModalItem}
                handleClose={() => setShowApplicationModal(false)}
            />
        </div>
    );
}

function VoteForm({
    candidates,
    min_candidates,
    rng_seed,
    election_id,
    has_voted,
}) {
    const [stateful_has_voted, setStatefulHasVoted] = useState(has_voted);
    const [pass_code, setPasscode] = useState(null);
    const [initial_ranked_kerberoses, setInitialRankedKerberoses] =
        useState(null);
    const [inputPasscode, setInputPasscode] = useState("");
    const [acknowledgePasscode, setAcknowledgePasscode] = useState(null);
    const [isPasscodeBeingSubmitted, setIsPasscodeBeingSubmitted] =
        useState(false);

    async function onPasscodeSubmit() {
        // Validate passcode before user
        setIsPasscodeBeingSubmitted(true);
        const previous_vote_promise = axios.get(
            `${endpoint}/hall_councilor/vote`,
            {
                params: {
                    election_id: election_id,
                    pass_code: inputPasscode,
                },
            },
        );

        toast.promise(previous_vote_promise, {
            loading: "Validating passcode...",
            success: "Successfully validated passcode!",
            error: "Failed to validate passcode!",
        });
        try {
            const previous_vote = await previous_vote_promise;
            const ranked_kerberoses =
                previous_vote.data.ranked_candidate_kerberoses;
            setPasscode(inputPasscode);
            setInitialRankedKerberoses(ranked_kerberoses);
        } catch (e) {
            console.error(e);
        } finally {
            setIsPasscodeBeingSubmitted(false);
        }
    }
    function on_vote_submit(pass_code) {
        setAcknowledgePasscode(pass_code);
    }

    function on_acknowledge_pass_code() {
        setPasscode(null);
        setInitialRankedKerberoses(null);
        setInputPasscode("");
        setStatefulHasVoted(true);
        setAcknowledgePasscode(null);
    }

    const passcode_component = (
        <div>
            <div>
                <p>
                    You have previously voted. Please provide your passcode to
                    edit your vote.
                </p>
            </div>
            <input
                className="form-control mb-3"
                value={inputPasscode}
                onChange={(e) => {
                    setInputPasscode(e.target.value);
                }}
            />
            <Button
                onClick={() => onPasscodeSubmit()}
                disabled={isPasscodeBeingSubmitted}
            >
                {!isPasscodeBeingSubmitted && "Submit"}
                {isPasscodeBeingSubmitted && (
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                )}
            </Button>
        </div>
    );

    const acknowledge_pass_code_component = (
        <div>
            <h5>Notice</h5>
            <p>
                Your vote for hall councilor has been recorded. An email has
                been sent to confirm your submission. Should you wish to change
                your vote later, please make sure to enter your passcode when
                voting or you will be unable to change your vote.
            </p>
            <p>
                Please keep this code in your records until results have been
                announced. While it has been emailed to you,{" "}
                <b>it will not be shown again here.</b>
            </p>
            <p>
                <b>Your Passcode:</b>
                <code>{acknowledgePasscode}</code>
            </p>
            <Button onClick={() => on_acknowledge_pass_code()}>Ok</Button>
        </div>
    );

    const show_voting_ui =
        (stateful_has_voted && pass_code !== null) || !stateful_has_voted;
    const show_acknowledge_pass_code = acknowledgePasscode !== null;

    return (
        <>
            {show_voting_ui && !show_acknowledge_pass_code && (
                <DragAndDropForm
                    candidates={candidates}
                    min_candidates={min_candidates}
                    rng_seed={rng_seed}
                    election_id={election_id}
                    pass_code={pass_code}
                    initial_ranked_kerberoses={initial_ranked_kerberoses}
                    on_vote_submit={on_vote_submit}
                />
            )}
            {show_acknowledge_pass_code && acknowledge_pass_code_component}
            {!show_voting_ui &&
                !show_acknowledge_pass_code &&
                passcode_component}
            <div>
                <Toaster />
            </div>
        </>
    );
}

export { VoteForm };
